import TrebbleClientAPI from 'models/helper/TrebbleClientAPI';
import { S3_PREFIX_TO_CLOUDFRONT_PREFIX } from 'models/audioEditor/AudioBufferCache';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 15;
const DEFAULT_TARGET_PEAKS = 8000;
const GENERATED_PEAKS_CACHE = new WeakMap();

function convertToCloudFrontUrl(url) {
    if (!url) return url;
    for (const s3Prefix in S3_PREFIX_TO_CLOUDFRONT_PREFIX) {
        if (url === s3Prefix || (url.startsWith(s3Prefix) && url[s3Prefix.length] === '/')) {
            return S3_PREFIX_TO_CLOUDFRONT_PREFIX[s3Prefix] + url.slice(s3Prefix.length);
        }
    }
    return url;
}

function getOrCreateGeneratedPeaksCacheEntry(audioBuffer) {
    let cacheEntry = GENERATED_PEAKS_CACHE.get(audioBuffer);
    if (!cacheEntry) {
        cacheEntry = {};
        GENERATED_PEAKS_CACHE.set(audioBuffer, cacheEntry);
    }
    return cacheEntry;
}

/**
 * Helper class for waveform peak data operations.
 * Handles loading server-generated peaks and format conversion
 * between BBC waveform-data v2 format and WaveSurfer float format.
 */
class PeaksWaveformHelper {

    /**
     * Load pre-generated waveform peaks from the server.
     * Polls until peaks are available or max attempts reached.
     *
     * @param {string} audioUrl - The audio file URL
     * @param {Object} [options] - Options
     * @param {number} [options.maxAttempts=15] - Max polling attempts
     * @param {number} [options.pollIntervalMs=2000] - Polling interval in ms
     * @param {Function} [options.shouldContinue] - Called before each attempt; return false to abort
     * @returns {Promise<Object|null>} Peaks data object or null if unavailable
     */
    static async loadServerPeaks(audioUrl, options = {}) {
        const maxAttempts = options.maxAttempts || MAX_POLL_ATTEMPTS;
        const pollIntervalMs = options.pollIntervalMs || POLL_INTERVAL_MS;
        const shouldContinue = options.shouldContinue || (() => true);
        const api = TrebbleClientAPI.getInstance();

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            if (!shouldContinue()) return null;

            try {
                const result = await api.downloadWaveformPeaks(audioUrl);

                // Check for inline peaks data first
                const inlineData = result && (result.data || result.dataPoints);
                if (inlineData && Array.isArray(inlineData) && inlineData.length > 0) {
                    return { data: inlineData, channels: result.channels || 1 };
                }

                // If server returned a URL to the peaks file on S3, fetch it via CloudFront
                if (result && result.peaksFileUrl) {
                    const peaksUrl = convertToCloudFrontUrl(result.peaksFileUrl);
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), 5000);
                    try {
                        const peaksResponse = await fetch(peaksUrl, { signal: controller.signal });
                        clearTimeout(timeoutId);
                        if (peaksResponse.ok) {
                            const peaksJson = await peaksResponse.json();
                            const fetchedData = peaksJson.data || peaksJson.dataPoints;
                            if (fetchedData && Array.isArray(fetchedData) && fetchedData.length > 0) {
                                return { data: fetchedData, channels: peaksJson.channels || 1 };
                            }
                        }
                    } catch (fetchError) {
                        clearTimeout(timeoutId);
                        // Continue polling on fetch timeout or network error
                    }
                }
            } catch (e) {
                // Continue polling on error
            }

            if (attempt < maxAttempts && shouldContinue()) {
                await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
            }
        }

        return null;
    }

    /**
     * Convert BBC waveform-data v2 JSON (Int8 min/max pairs) to WaveSurfer peaks format.
     * Downsamples to targetPeaks buckets when the payload exceeds that limit.
     *
     * BBC format:  { data: [min0, max0, min1, max1, ...] } — Int8 values (-128 to 127)
     * WaveSurfer format: Array<Float32Array> — single channel, values in [0, 1.0]
     *
     * Uses max absolute amplitude per bucket for symmetric waveform rendering.
     *
     * @param {Object} bbcJson - BBC waveform-data v2 JSON with `data` array
     * @param {number} [targetPeaks] - Max output buckets; omit to return all buckets unchanged
     * @returns {Array<Float32Array>} Single Float32Array wrapped in array for WaveSurfer
     */
    static convertBBCPeaksToWaveSurferPeaks(bbcJson, targetPeaks) {
        const rawData = bbcJson.data;
        const channels = bbcJson.channels || 1;
        const stride = 2 * channels;
        const numBuckets = Math.floor(rawData.length / stride);
        const normalizedTargetPeaks = Number.isFinite(targetPeaks)
            ? Math.max(1, Math.ceil(targetPeaks))
            : numBuckets;
        const outputBuckets = Math.min(numBuckets, normalizedTargetPeaks);
        const peaks = new Float32Array(outputBuckets);

        if (outputBuckets === numBuckets) {
            // Fast path: no downsampling needed. Merge all channels per frame via max-abs.
            for (let i = 0; i < numBuckets; i++) {
                const frameOffset = i * stride;
                let bucketMax = 0;
                for (let c = 0; c < channels; c++) {
                    const min = rawData[frameOffset + c * 2];
                    const max = rawData[frameOffset + c * 2 + 1];
                    const amplitude = Math.max(Math.abs(min), Math.abs(max));
                    if (amplitude > bucketMax) bucketMax = amplitude;
                }
                peaks[i] = bucketMax / 128;
            }
        } else {
            // Downsample: for each output bucket find the max abs amplitude across its input buckets,
            // merged across all channels.
            // bucketsPerPeak is fractional, so we floor the boundaries and guard against a zero-width
            // window with Math.max(startBucket + 1, ...) in case floating-point rounding collapses them.
            // Math.min(numBuckets, ...) ensures we never read past the end of the data.
            const bucketsPerPeak = numBuckets / outputBuckets;
            for (let i = 0; i < outputBuckets; i++) {
                const startBucket = Math.floor(i * bucketsPerPeak);
                const endBucket = Math.min(numBuckets, Math.max(startBucket + 1, Math.floor((i + 1) * bucketsPerPeak)));
                let maxAbs = 0;
                for (let bucket = startBucket; bucket < endBucket; bucket++) {
                    const frameOffset = bucket * stride;
                    for (let c = 0; c < channels; c++) {
                        const min = rawData[frameOffset + c * 2];
                        const max = rawData[frameOffset + c * 2 + 1];
                        const amplitude = Math.max(Math.abs(min), Math.abs(max)) / 128;
                        if (amplitude > maxAbs) maxAbs = amplitude;
                    }
                }
                peaks[i] = maxAbs;
            }
        }

        return [peaks];
    }

    /**
     * Async variant of convertBBCPeaksToWaveSurferPeaks that offloads the work to the
     * waveform peaks worker when available, falling back to the synchronous path.
     *
     * Prefer this over the synchronous version for large server-peaks payloads so the
     * main thread is not blocked during conversion and downsampling.
     *
     * @param {Object} bbcJson - BBC waveform-data v2 JSON with `data` array
     * @param {number} [targetPeaks] - Max output buckets; omit to return all buckets unchanged
     * @returns {Promise<Array<Float32Array>>} Single Float32Array wrapped in array for WaveSurfer
     */
    static convertBBCPeaksToWaveSurferPeaksAsync(bbcJson, targetPeaks) {
        const rawData = bbcJson.data;
        const channels = bbcJson.channels || 1;

        return new Promise((resolve, reject) => {
            let worker;
            let int8Data;
            try {
                // Convert the plain JS array to an Int8Array so it can be transferred without copying.
                int8Data = new Int8Array(rawData);
                worker = new Worker(new URL('workers/waveformPeaksWorker.js', import.meta.url));
            } catch (error) {
                reject(error);
                return;
            }

            const cleanup = () => {
                if (!worker) return;
                worker.onmessage = null;
                worker.onerror = null;
                worker.terminate();
                worker = null;
            };

            worker.onmessage = (event) => {
                cleanup();
                if (!event.data || !event.data.peaksBuffer) {
                    reject(new Error('Waveform peaks worker returned no peaks buffer'));
                    return;
                }
                resolve([new Float32Array(event.data.peaksBuffer)]);
            };

            worker.onerror = (error) => {
                cleanup();
                reject(error);
            };

            worker.postMessage(
                { bbcPeaksData: int8Data.buffer, channels, targetPeaks },
                [int8Data.buffer]
            );
        }).catch((error) => {
            console.warn('[PeaksWaveformHelper] Waveform peaks worker failed, falling back to sync conversion:', error);
            return this.convertBBCPeaksToWaveSurferPeaks(bbcJson, targetPeaks);
        });
    }

    /**
     * Generate downsampled waveform peaks from channel data.
     * Uses max absolute amplitude per bucket for symmetric rendering.
     *
     * @param {Float32Array} channelData - Channel samples to scan
     * @param {number} [targetPeaks=8000] - Number of peak buckets to generate
     * @returns {Array<Float32Array>} Single Float32Array wrapped in array for WaveSurfer
     */
    static generatePeaksFromChannelData(channelData, targetPeaks) {
        targetPeaks = targetPeaks || DEFAULT_TARGET_PEAKS;
        const samplesPerPeak = Math.max(1, Math.floor(channelData.length / targetPeaks));
        const numPeaks = Math.ceil(channelData.length / samplesPerPeak);
        const peaks = new Float32Array(numPeaks);

        for (let i = 0; i < numPeaks; i++) {
            const start = i * samplesPerPeak;
            const end = Math.min(start + samplesPerPeak, channelData.length);
            let maxAbs = 0;
            for (let j = start; j < end; j++) {
                const abs = Math.abs(channelData[j]);
                if (abs > maxAbs) maxAbs = abs;
            }
            peaks[i] = maxAbs;
        }

        return [peaks];
    }

    /**
     * Generate downsampled waveform peaks from an AudioBuffer for WaveSurfer.
     * Uses max absolute amplitude per bucket for symmetric rendering.
     *
     * @param {AudioBuffer} audioBuffer - Decoded audio buffer from Web Audio API
     * @param {number} [targetPeaks=8000] - Number of peak buckets to generate
     * @returns {Array<Float32Array>} Single Float32Array wrapped in array for WaveSurfer
     */
    static generatePeaksFromAudioBuffer(audioBuffer, targetPeaks) {
        const numberOfChannels = audioBuffer.numberOfChannels;
        if (numberOfChannels <= 1) {
            return this.generatePeaksFromChannelData(audioBuffer.getChannelData(0), targetPeaks);
        }

        const effectiveTarget = targetPeaks || DEFAULT_TARGET_PEAKS;
        const length = audioBuffer.length;
        const samplesPerPeak = Math.max(1, Math.floor(length / effectiveTarget));
        const numPeaks = Math.ceil(length / samplesPerPeak);
        const peaks = new Float32Array(numPeaks);

        const channelArrays = new Array(numberOfChannels);
        for (let c = 0; c < numberOfChannels; c++) {
            channelArrays[c] = audioBuffer.getChannelData(c);
        }

        for (let i = 0; i < numPeaks; i++) {
            const start = i * samplesPerPeak;
            const end = Math.min(start + samplesPerPeak, length);
            let maxAbs = 0;
            for (let c = 0; c < numberOfChannels; c++) {
                const channelData = channelArrays[c];
                for (let j = start; j < end; j++) {
                    const abs = Math.abs(channelData[j]);
                    if (abs > maxAbs) maxAbs = abs;
                }
            }
            peaks[i] = maxAbs;
        }

        return [peaks];
    }

    static _generatePeaksFromAudioBufferInWorker(audioBuffer, targetPeaks) {
        return new Promise(function (resolve, reject) {
            let worker;
            try {
                worker = new Worker(new URL('workers/waveformPeaksWorker.js', import.meta.url));
            } catch (error) {
                reject(error);
                return;
            }

            const cleanup = function () {
                if (!worker) return;
                worker.onmessage = null;
                worker.onerror = null;
                worker.terminate();
                worker = null;
            };

            worker.onmessage = function (event) {
                cleanup();
                if (!event.data || !event.data.peaksBuffer) {
                    reject(new Error('Waveform peaks worker returned no peaks buffer'));
                    return;
                }
                resolve([new Float32Array(event.data.peaksBuffer)]);
            };

            worker.onerror = function (error) {
                cleanup();
                reject(error);
            };

            const channelDataCopy = new Float32Array(audioBuffer.getChannelData(0));
            worker.postMessage(
                {
                    channelData: channelDataCopy.buffer,
                    targetPeaks: targetPeaks || DEFAULT_TARGET_PEAKS
                },
                [channelDataCopy.buffer]
            );
        });
    }

    /**
     * Async variant that offloads peak generation to a worker when available.
     * Results are cached per AudioBuffer + targetPeaks so reopening the same popup
     * does not rescan the waveform samples again.
     *
     * @param {AudioBuffer} audioBuffer - Decoded audio buffer from Web Audio API
     * @param {number} [targetPeaks=8000] - Number of peak buckets to generate
     * @returns {Promise<Array<Float32Array>>} Single Float32Array wrapped in array
     */
    static generatePeaksFromAudioBufferAsync(audioBuffer, targetPeaks) {
        targetPeaks = targetPeaks || DEFAULT_TARGET_PEAKS;

        const cacheEntry = getOrCreateGeneratedPeaksCacheEntry(audioBuffer);
        if (cacheEntry[targetPeaks]) {
            return cacheEntry[targetPeaks];
        }

        const peaksPromise = this._generatePeaksFromAudioBufferInWorker(audioBuffer, targetPeaks)
            .catch(() => this.generatePeaksFromAudioBuffer(audioBuffer, targetPeaks));

        cacheEntry[targetPeaks] = peaksPromise;
        return peaksPromise;
    }
}

export default PeaksWaveformHelper;
